import { useEffect, useState } from 'react';
import { apiBaseUrl, apiService } from '../../services/api';

// Appels du module Exploration (routes /projects/{id}/exploration).

const base = (projectId) => `/projects/${projectId}/exploration`;

export const explorationApi = {
  status: (projectId) => apiService.get(`${base(projectId)}/status`),
  enable: (projectId) => apiService.post(`${base(projectId)}/enable`, {}),
  disable: (projectId) => apiService.post(`${base(projectId)}/disable`, {}),
  bundle: (projectId) => apiService.get(base(projectId)),
  settings: (projectId, settings) => apiService.put(`${base(projectId)}/settings`, { settings }),
  ops: (projectId, operations, motif, dryRun = false) => apiService.post(`${base(projectId)}/ops`, { operations, motif, dry_run: dryRun }),
  history: (projectId, { nodeId, before, important = true, limit = 60 } = {}) => {
    const query = new URLSearchParams({ limit: String(limit), important: String(important) });
    if (nodeId) query.set('node_id', String(nodeId));
    if (before) query.set('before', String(before));
    return apiService.get(`${base(projectId)}/history?${query}`);
  },
  replay: (projectId, at) => apiService.get(`${base(projectId)}/replay?at=${encodeURIComponent(at)}`),
  diff: (projectId, start, end) => apiService.get(`${base(projectId)}/diff?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`),
  landmarks: (projectId) => apiService.get(`${base(projectId)}/landmarks`),
  inbox: (projectId, status = 'open') => apiService.get(`${base(projectId)}/inbox?status=${status}`),
  accept: (projectId, id, body = {}) => apiService.post(`${base(projectId)}/inbox/${id}/accept`, body),
  reject: (projectId, id, reason) => apiService.post(`${base(projectId)}/inbox/${id}/reject`, { reason }),
  reviewLock: (projectId, body) => apiService.post(`${base(projectId)}/assist/lock`, body),
  classifyLock: (projectId, title) => apiService.post(`${base(projectId)}/assist/classify-lock`, { title }),
  suggestLinks: (projectId, nodeId) => apiService.get(`${base(projectId)}/assist/links/${nodeId}`),
  duplicates: (projectId, type, title, excludeId) => apiService.post(`${base(projectId)}/assist/duplicates`, { type, title, exclude_id: excludeId }),
  ask: (projectId, text, selection) => apiService.post(`${base(projectId)}/assist/ask`, { text, selection }),
  read: (projectId, body) => apiService.post(`${base(projectId)}/assist/read`, body),
  synthesis: (projectId) => apiService.post(`${base(projectId)}/assist/synthesis`, {}),
  scan: (projectId) => apiService.post(`${base(projectId)}/scan`, {}),
  // Copilote : ce que Nexus pense de chaque élément, ses gestes (accepter, ignorer, répondre, défaire un lien).
  copilot: (projectId) => apiService.get(`${base(projectId)}/copilot`),
  copilotAct: (projectId, nodeId, action, body = {}) => apiService.post(`${base(projectId)}/copilot/${nodeId}/${action}`, body),
  copilotDraft: (projectId, type, title, nodeId) => apiService.post(`${base(projectId)}/copilot/draft`, { type, title, node_id: nodeId }),
  search: (projectId, q) => apiService.get(`${base(projectId)}/search?q=${encodeURIComponent(q)}`),
  // Nouvel artefact avec Nexus : la question suivante et la fiche, lancées ensemble à chaque réponse ; puis la création.
  artefactNext: (projectId, body) => apiService.post(`${base(projectId)}/artefact-draft/next`, body),
  artefactFill: (projectId, body) => apiService.post(`${base(projectId)}/artefact-draft/fill`, body),
  artefactCreate: (projectId, draft) => apiService.post(`${base(projectId)}/artefact-draft/create`, { draft }),
  report: async (projectId, { client = false } = {}) => {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${apiBaseUrl}${base(projectId)}/report?client=${client}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error('Rapport indisponible');
    return response.text();
  },
  upload: (projectId, nodeId, files) => upload(`${base(projectId)}/nodes/${nodeId}/files`, files, 'files'),
  importAinov: (projectId, file) => upload(`${base(projectId)}/import`, [file], 'file'),
  me: () => apiService.get('/exploration/me'),
  access: () => apiService.get('/exploration/access'),
  setAccess: (employeeId, allowed) => apiService.put(`/exploration/access/${employeeId}`, { allowed }),
};

async function upload(endpoint, files, field) {
  const token = localStorage.getItem('auth_token');
  const form = new FormData();
  [...files].forEach((file) => form.append(field, file));
  const response = await fetch(`${apiBaseUrl}${endpoint}`, {
    method: 'POST', body: form, headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(typeof error.detail === 'string' ? error.detail : 'Envoi impossible');
  }
  return response.json();
}

export const fileUrl = (file) => {
  if (!file?.url) return null;
  return /^https?:\/\//.test(file.url) ? file.url : `${apiBaseUrl}${file.url}`;
};

// Accès au module (une requête par session) : sert à cacher l'ancien C/K et l'ancien onglet Attributs.
let accessPromise = null;
export const useExplorationAccess = () => {
  const [access, setAccess] = useState(false);
  useEffect(() => {
    let alive = true;
    accessPromise = accessPromise || explorationApi.me().catch(() => ({ access: false }));
    accessPromise.then((me) => { if (alive) setAccess(!!me?.access); });
    return () => { alive = false; };
  }, []);
  return access;
};
